/**
 * Handing a link to the system share sheet — the invite link, today.
 *
 * Under Capacitor this becomes `@capacitor/share`. On the web it is the Web
 * Share API, which a desktop browser may not have at all: `available()` says
 * so up front, and the caller copies instead.
 */
export interface ShareContent {
  url: string
  text?: string
}

/**
 * `dismissed` is the user closing the sheet, which is an answer; `failed` is
 * the platform refusing, which the caller covers by copying.
 */
export type ShareOutcome = 'shared' | 'dismissed' | 'failed'

export interface ShareApi {
  available(content: ShareContent): boolean
  share(content: ShareContent): Promise<ShareOutcome>
}

export const webShare: ShareApi = {
  available(content) {
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false
    try {
      return navigator.canShare?.(content) ?? true
    } catch {
      return false
    }
  },
  async share(content) {
    try {
      await navigator.share(content)
      return 'shared'
    } catch (error) {
      return error instanceof DOMException && error.name === 'AbortError' ? 'dismissed' : 'failed'
    }
  },
}
